import cx from "classnames";
import { FC, useContext, useId } from "react";
import {
  DEFAULT_MAP_STYLE_BOUNDARY_LAYERS,
  DEFAULT_MAP_STYLE_SHOW_SCALE_BAR,
  DEFAULT_MAP_STYLE_ZOOM_LEVEL,
  MultiMapExportAction,
  MultiMapExportState,
} from "@app/projects/network-assessment/multi-map-export/state-management/multiMapExportStateManagement";
import {
  MAP_STYLE_BOUNDARY_LABEL_OPTIONS,
  MAP_STYLE_BOUNDARY_LINE_OPTIONS,
  MAP_STYLE_ZOOM_OPTIONS,
} from "@app/projects/network-assessment/multi-map-export/utils/mapStyleOptions";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESRadioButton } from "@app/shared/qes-standards/forms/RadioButton";
import {
  MultiMapExportBoundaryOption,
  MultiMapExportMapStyleOption,
  MultiMapExportZoomLevel,
} from "@app/projects/network-assessment/multi-map-export/interfaces";
import { MultiMapExportContext } from "@app/projects/network-assessment/multi-map-export/MultiMapExportContext";
import { MultiMapExportSection } from "@app/projects/network-assessment/multi-map-export/components/MultiMapExportSection";
import { isBoundaryLabelAvailable, toggleBoundaryLayer } from "@app/projects/network-assessment/multi-map-export/utils/mapStyleSelection";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { CountyShadingGroup } from "@app/projects/network-assessment/multi-map-export/components/CountyShadingGroup";

const SECTION_NUMBER = 6;
const TEST_ID = "multi-map-export__map-style";
const SCALE_BAR_TEST_ID = `${TEST_ID}-scale-bar`;

const ZOOM_RADIO_NAME = "multi-map-export-map-style-zoom-level";

export const MapStyleSection: FC<React.PropsWithChildren<unknown>> = () => {
  const { selections, selectionsChanged } = useContext(MultiMapExportContext);
  const boundariesLabelId = useId();
  const boundaryLabelsLabelId = useId();
  const scaleBarLabelId = useId();
  const zoomLevelLabelId = useId();

  const selectedLayers = selections?.selectedBoundaryLayers ?? DEFAULT_MAP_STYLE_BOUNDARY_LAYERS;
  const selectedZoomLevel = selections?.selectedZoomLevel ?? DEFAULT_MAP_STYLE_ZOOM_LEVEL;
  const showScaleBar = selections?.showScaleBar ?? DEFAULT_MAP_STYLE_SHOW_SCALE_BAR;

  const mapStyleChanged = (value: MultiMapExportState) => selectionsChanged?.({
    type: MultiMapExportAction.MapStyleChanged,
    value,
  });

  // Never pass this straight to .map: the second argument would be the array index.
  const renderBoundaryCheckbox = (option: MultiMapExportBoundaryOption, isAvailable: boolean) => (
    <QESCheckbox
      checked={selectedLayers.includes(option.layer)}
      containerClassName={cx("multi-map-export__map-style-option", {
        "multi-map-export__map-style-option--child": option.isChildRow,
      })}
      disabled={!isAvailable}
      key={option.layer}
      label={option.label}
      onChange={(event) => mapStyleChanged({
        selectedBoundaryLayers: toggleBoundaryLayer(selectedLayers, option.layer, event.target.checked),
      })}
      testId={`${TEST_ID}-option--${option.layer}`}
    />
  );

  // Not descriptiveText, which renders the description as a visible line under the option.
  const renderZoomOption = (option: MultiMapExportMapStyleOption<MultiMapExportZoomLevel>) => (
    <TooltipWrapper key={option.value} position="top" text={option.hoverDescription ?? ""}>
      <QESRadioButton
        checked={selectedZoomLevel === option.value}
        label={option.label}
        name={ZOOM_RADIO_NAME}
        onChange={() => mapStyleChanged({ selectedZoomLevel: option.value })}
        testId={`${TEST_ID}-zoom--${option.value}`}
        value={option.value}
      />
    </TooltipWrapper>
  );

  return (
    <MultiMapExportSection
      sectionNumber={SECTION_NUMBER}
      subHeader="Choose how the maps look. Applied to every map."
      testId="multi-map-export__map-style-section"
      title="Map Styling"
    >
      <div className="multi-map-export__map-style-columns">
        <div aria-labelledby={boundariesLabelId} className="multi-map-export__map-style-group" role="group">
          <span className="multi-map-export__field-label" id={boundariesLabelId}>Boundaries</span>
          {MAP_STYLE_BOUNDARY_LINE_OPTIONS.map((option) => renderBoundaryCheckbox(option, true))}
        </div>
        <div aria-labelledby={boundaryLabelsLabelId} className="multi-map-export__map-style-group" role="group">
          <span className="multi-map-export__field-label" id={boundaryLabelsLabelId}>Boundary Labels</span>
          {MAP_STYLE_BOUNDARY_LABEL_OPTIONS.map((option) => renderBoundaryCheckbox(option, isBoundaryLabelAvailable(selectedLayers, option)))}
        </div>
        <div aria-labelledby={zoomLevelLabelId} className="multi-map-export__map-style-group" role="radiogroup">
          <span className="multi-map-export__field-label" id={zoomLevelLabelId}>Zoom Level</span>
          {MAP_STYLE_ZOOM_OPTIONS.map(renderZoomOption)}
        </div>
        <CountyShadingGroup />
        <div
          aria-labelledby={scaleBarLabelId}
          className="multi-map-export__map-style-group"
          role="group"
        >
          <span className="multi-map-export__field-label" id={scaleBarLabelId}>Scale Bar</span>
          <QESCheckbox
            checked={showScaleBar}
            containerClassName="multi-map-export__map-style-option"
            label="Show scale bar"
            onChange={(event) => mapStyleChanged({ showScaleBar: event.target.checked })}
            testId={SCALE_BAR_TEST_ID}
          />
        </div>
      </div>
    </MultiMapExportSection>
  );
};

export default MapStyleSection;
